import { gql, useQuery } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { parse, print, visit } from 'graphql';
import React from 'react';

import { CollectionEntryState } from 'src/app/graph/explorerPage/hooks/useExplorerState/useEditorTabState/collectionEntryState';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { SaveCollectionEntryModal } from 'src/app/graph/shared/saveCollectionEntryModal/SaveCollectionEntryModal';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const SaveCollectionEntryFromOperationsPaneModal = ({
  isOpen,
  onClose,
  query,
}: {
  isOpen: boolean;
  onClose: () => void;
  query: string;
}) => {
  const graphRef = useGraphRef();

  const { data } = useQuery<
    GraphQLTypes.SaveCollectionEntryFromOperationsPaneQuery,
    GraphQLTypes.SaveCollectionEntryFromOperationsPaneQueryVariables
  >(
    gql`
      query SaveCollectionEntryFromOperationsPaneQuery(
        $graphId: ID!
        $operationId: ID!
      ) {
        service(id: $graphId) {
          id
          operation(id: $operationId) {
            id
            name
            signature
          }
        }
      }
    `,
    {
      variables: {
        graphId: graphRef?.graphId as string,
        operationId: query as string,
      },
      skip: !graphRef?.graphId || !query,
    },
  );

  const trackCustomEvent = useTrackCustomEvent();

  const onCollectionEntrySaved = React.useCallback(
    ({
      collectionType,
    }: {
      collectionType: 'sandbox' | 'shared' | 'personal' | 'mcp';
    }) =>
      trackCustomEvent({
        action: 'save_collection_entry_from_operations_pane',
        category: 'Operation Collections',
        label: collectionType,
      }),
    [trackCustomEvent],
  );

  const operationState = React.useMemo((): CollectionEntryState | null => {
    if (!data?.service?.operation?.signature) {
      return null;
    }
    const parsedOperationBody = parse(data.service.operation.signature);
    const variables: string[] = [];
    visit(parsedOperationBody, {
      VariableDefinition: (node) => {
        variables.push(node.variable.name.value);
      },
    });
    return {
      operation: print(parsedOperationBody),
      headers: [],
      variables: JSON.stringify(
        Object.fromEntries(variables.map((variable) => [variable, null])),
        null,
        2,
      ),
    };
  }, [data]);

  if (!data?.service?.operation || !operationState) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Save Operation</ModalTitle>
          </ModalHeader>
          <ModalBody>Error</ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={onClose} type="button">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <SaveCollectionEntryModal
      isOpen={isOpen}
      title="Save a copy of this operation"
      graphRef={graphRef}
      operationState={operationState}
      initialCollectionEntryName={data.service.operation.name ?? ''}
      onCollectionEntrySaved={onCollectionEntrySaved}
      onClose={onClose}
    />
  );
};
